/**
 * „Grundbestand importieren“: rund 300 allgemeine Zutaten und Haushaltswaren
 * nach Warengruppen. Vorausgewählt ist alles, was es noch nicht gibt;
 * Vorhandenes ist markiert und wird nicht doppelt angelegt.
 */
import { SHOPPING_CATEGORIES } from '@cookbook/domain';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../../api/client.ts';
import { texts } from '../../texts/de.ts';

/** Eintrag, wie der Server ihn liefert. */
interface BaseStockEntry {
  name: string;
  category: string;
  unit: string;
  synonyms: string[];
  exists: boolean;
}

interface BaseStockDialogProps {
  readonly onClose: () => void;
  /** Nach dem Import, zum Beispiel um die Liste neu zu laden. */
  readonly onImported: () => void;
}

/** Dialog zum Importieren des Grundbestands. */
export function BaseStockDialog({ onClose, onImported }: BaseStockDialogProps) {
  const [items, setItems] = useState<BaseStockEntry[] | null>(null);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: BaseStockEntry[] }>('/api/products/base-stock')
      .then((response) => {
        setItems(response.items);
        setChosen(new Set(response.items.filter((item) => !item.exists).map((item) => item.name)));
      })
      .catch((caught: unknown) =>
        setMessage(caught instanceof ApiError ? caught.message : texts.common.error),
      );
  }, []);

  const groups = useMemo(
    () =>
      SHOPPING_CATEGORIES.map((category) => ({
        ...category,
        items: (items ?? []).filter((item) => item.category === category.id),
      })).filter((group) => group.items.length > 0),
    [items],
  );

  /** Wählt Einträge an oder ab. */
  function toggle(names: readonly string[], on: boolean) {
    const next = new Set(chosen);
    for (const name of names) {
      if (on) {
        next.add(name);
      } else {
        next.delete(name);
      }
    }
    setChosen(next);
  }

  async function importChosen() {
    setBusy(true);
    setMessage(null);
    try {
      const response = await api.post<{ created: number; skipped: number }>('/api/products/base-stock', {
        names: [...chosen],
      });
      setResult(
        texts.baseStock.result
          .replace('{created}', String(response.created))
          .replace('{skipped}', String(response.skipped)),
      );
      onImported();
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="base-stock-title"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-3xl bg-surface-raised p-5 shadow-xl sm:rounded-3xl"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 id="base-stock-title" className="text-lg font-bold">
            {texts.baseStock.title}
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg px-2 py-1 text-sm text-content-muted">
            {texts.common.close}
          </button>
        </div>
        <p className="mb-3 text-sm text-content-muted">{texts.baseStock.hint}</p>

        {result !== null ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{result}</p>
            <p className="text-sm text-content-muted">{texts.baseStock.next}</p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
            >
              {texts.common.close}
            </button>
          </div>
        ) : items === null ? (
          <p className="text-sm text-content-muted">{message ?? texts.common.loading}</p>
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
              {groups.map((group) => {
                const open = group.items.filter((item) => !item.exists);
                const all = open.length > 0 && open.every((item) => chosen.has(item.name));
                return (
                  <details key={group.id} className="rounded-xl border border-border-subtle">
                    <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={all}
                        disabled={open.length === 0}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) =>
                          toggle(
                            open.map((item) => item.name),
                            event.target.checked,
                          )
                        }
                        aria-label={texts.baseStock.allOf.replace('{group}', group.label)}
                        className="accent-accent"
                      />
                      <span className="flex-1 font-medium">{group.label}</span>
                      <span className="text-xs text-content-muted">
                        {open.filter((item) => chosen.has(item.name)).length} / {group.items.length}
                      </span>
                    </summary>
                    <ul className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 pb-3 text-sm">
                      {group.items.map((item) => (
                        <li key={item.name}>
                          <label
                            className={`flex items-center gap-2 ${item.exists ? 'text-content-muted' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={item.exists || chosen.has(item.name)}
                              disabled={item.exists}
                              onChange={(event) => toggle([item.name], event.target.checked)}
                              className="accent-accent"
                            />
                            <span className="truncate">
                              {item.name}
                              {item.exists ? ` (${texts.baseStock.exists})` : ''}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </details>
                );
              })}
            </div>
            {message === null ? null : <p className="mt-2 text-sm text-accent-strong">{message}</p>}
            <button
              type="button"
              onClick={() => void importChosen()}
              disabled={busy || chosen.size === 0}
              className="mt-3 w-full rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent disabled:opacity-50"
            >
              {busy
                ? texts.baseStock.importing
                : texts.baseStock.import.replace('{count}', String(chosen.size))}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
